import * as CL from '@design-system/component-library';
import {
  accordingToTheBrandMsg,
  cheapestFirstMsg,
  mostExpensiveFirstMsg,
  newestFirstMsg,
  t,
} from '../../common/i18n/index.js';
import { getLocalizedString } from '../../common/utils/productOfferingsUtils.js';
import { getDeviceBrand, getOfferPriceEuros } from './productGridUtilsCoCo.js';
import type { DeviceProductOffer } from './productGridUtilsCoCo.js';

export enum SortType {
  ORDER_NEWEST = 'ORDER_NEWEST',
  ORDER_ALPHABETICAL = 'ORDER_ALPHABETICAL',
  ORDER_CHEAPEST = 'ORDER_CHEAPEST',
  ORDER_COSTLIEST = 'ORDER_COSTLIEST',
}

type SortOrder = {
  displayText: () => string;
  sortFn: (x: DeviceProductOffer, y: DeviceProductOffer) => number;
};

type SortOrders = { [key in SortType]: SortOrder };

const sortByPrice = (x: DeviceProductOffer, y: DeviceProductOffer) => {
  const xP = getOfferPriceEuros(x) ?? Number.POSITIVE_INFINITY;
  const yP = getOfferPriceEuros(y) ?? Number.POSITIVE_INFINITY;
  return xP - yP;
};

const sortOrders: SortOrders = {
  ORDER_NEWEST: {
    displayText: () => t.Z6SX(newestFirstMsg),
    // TODO: check for creation date till then as DeviceOfferLike has no creation timestamp; fall back to id-based stable order.
    sortFn: (x: DeviceProductOffer, y: DeviceProductOffer) => y.id.localeCompare(x.id),
  },
  ORDER_ALPHABETICAL: {
    displayText: () => t.VLDA(accordingToTheBrandMsg),
    sortFn: (x: DeviceProductOffer, y: DeviceProductOffer) =>
      `${getDeviceBrand(x)}${getLocalizedString(x.name)}`.localeCompare(
        `${getDeviceBrand(y)}${getLocalizedString(y.name)}`
      ),
  },
  ORDER_CHEAPEST: {
    displayText: () => t.MA8D(cheapestFirstMsg),
    sortFn: sortByPrice,
  },
  ORDER_COSTLIEST: {
    displayText: () => t.FVI5(mostExpensiveFirstMsg),
    sortFn: (x: DeviceProductOffer, y: DeviceProductOffer) => sortByPrice(y, x),
  },
};

export const sortProducts = (sortType: SortType) => sortOrders[sortType].sortFn;

export const getSortOrderOptions = () =>
  Object.entries(sortOrders).map(entry => ({ value: entry[0], label: entry[1].displayText() }));

export const ProductGridSortCoCo = ({
  onChange,
  selectedValue,
}: {
  onChange: (newValue: SortType) => void;
  selectedValue?: SortType;
}) => (
  <CL.Dropdown
    className="of-product-grid__sort-dropdown"
    items={getSortOrderOptions()}
    aria-label="sort"
    selectedValue={selectedValue ?? SortType.ORDER_NEWEST}
    onValueChange={listElement => {
      onChange(listElement.dataset.value as SortType);
    }}
  />
);
